import { floatVal, HashOf } from "@jamesgmarks/utilities";
import { Invoice } from "../../../entities/hydra";
import { formatDecimalToPercentage, roundFloatToCents } from "@hydra/internal";
import {
  IOrderCommand, IWhereCommand, TOrderDirection, TWhereCondition, TWhereOperator,
} from "@llws/dynamic-router-utils";

/**
 * Splits a string by its whitespace, and capitalizes the first letter of each word. All other letters will be returned lowercase.
 * @param str
 * @returns A Capitalized String
 */
export const capitalize = (str: string) => (
  str
    .trim()
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
);

/**
 * Checks if the input string can be parsed into a four-character integer.
 * @param str A user-inputted string intended to represent a year.
 * @returns A boolean indicating whether or not the string represents a valid year.
 */
export const validateYearInput = (str: string) => (
  /^[0-9]{0,4}$/
    .test(str.trim())
);

/**
 * Checks if the input string can be parsed into a valid month (1 - 12).
 * @param str A user-inputted string intended to represent a month.
 * @returns A boolean indicating whether or not the string represents a valid month.
 */
export const validateMonthInput = (str: string) => (
  /^([1-9](?!.)|1[0-2])?$/
    .test(str.trim())
);

/**
 * Checks if the input string can be parsed into a valid YYYY-MM-DD string. (Leading zeros are not optinal)
 * @param str A string intended to represent a date string.
 * @returns A boolean indicating whether or not the string represents a valid YYYY-MM-DD date string.
 */
export const isValidYmdString = (str: string) => (
  /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/
    .test(str.trim())
);

/**
 * Checks if the input string is in a valid ISO 8601 format.
 * @param str A string intended to represent a ISO date string.
 * @returns A boolean indicating whether or not the string represents a valid ISO date string.
 */
export const isValidIsoString = (str: string) => (
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)((-(\d{2}):(\d{2})|Z)?)$/
    .test(str.trim())
);
/** Provided a `Date` instance, sets the time of it to the last millisecond of that date. */
export const getEndOfDay = (date: Date) => new Date((new Date(date)).setHours(23, 59, 59, 999));

/** Converts a camelCase string into a capitalized one, with spaces and
 * where the first letter of each word is uppercase. Handles acronyms
 * where multiple upper-case letters may be placed in a row. */
export const camelCaseToCapitalized = (str: string) => {
  const spaced = (
    str
      .replace(/([A-Z])([a-z])/g, ' $1$2') // Handles `aTestVar`
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Handles `anACRONYMVar
  );

  return capitalize(spaced[0]) + spaced.slice(1);
};

/** Converts a capitalized string, where the first letter of each
 * word, only, is capitalized, into a camelCase version. */
export const capitalizedToCamelCase = (str: string) => {
  const spacesRemoved = str.replace(/\s([A-Z])/g, '$1');
  return spacesRemoved[0].toLowerCase() + spacesRemoved.slice(1);
};

/**
 * Extracts the date and/or time part of an ISO timestamp string, without conversion from UTC.
 * Some localized date values in the database become ISO strings upon retrieval.
 * This function avoids the offset upon parsing a `Date`.
 * @param str The ISO timestamp (e.g. '2022-08-24T00:00:00.000Z').
 * @param part Determines what to extract.
 * @returns If 'both' is chosen, an array of both elements, otherwise a `string`.
 */
export const getPartsFromIsoString = (
  str: string,
  part: 'date' | 'time' | 'both' = 'both',
): string | string[] => {
  const parts = str.split('T');

  if (parts.length !== 2) {
    throw new Error('Invalid ISO string provided!');
  }

  const partIndexMap = {
    date: 0,
    time: 1,
    both: 2,
  };

  return (
    parts[partIndexMap[part]] ?? parts
  );
};

/**
 * Takes an object with an `email` property and an optional `name` property.
 * Returns a formatted string with pertinent contact information.
 * @param contact Object with an email and optional name.
 * @returns Formatted string.
 */
export const getEmailStringFromContactInformation = (
  contact: { name?: string, email: string },
): string => (
  contact.name
    ? `${contact.name} <${contact.email}>`
    : `${contact.email}`
);

/**
 * return the up or down pointing black triangle
 * @param criteria the column name
 * @param sortCriteria the current column that is being sorted
 * @param sortDirection direction of the sort and the triangle
 * @returns return the up or down pointing black triangle
 */
export const showSortIconIfAvailable = (
  criteria: string,
  sortCriteria: string,
  sortDirection: 'ASC' | 'DESC',
) => {
  return (
    sortCriteria === criteria
      ? (sortDirection === 'ASC'
        ? String.fromCodePoint(9650)
        : String.fromCodePoint(9660))
      : null
  );
};

/**
 * Provides a functional reduce-like abstraction to work with arrays asynchronously and
 * sequentially.
 * #DuplicateFunction
 */
export const reduceAsync = async <T, U>(
  list: T[],
  handler: ((acc: U, curr: T, currIndex: number, array: T[]) => Promise<U>),
  initialValue: U,
) => {
  let result = initialValue;

  // eslint-disable-next-line no-restricted-syntax
  for (let i = 0; i < list.length; i++) {
    const value = list[i];
    // `await` will transform result of the function to the promise, even it is a synchronous call
    result = await handler(result, value, i, list); // eslint-disable-line no-await-in-loop
  }

  return result;
};

/**
 * Find number of months between two dates, exclusive.
 * @param startDate Start Date
 * @param endDate End Date
 * @returns
 */
export const getNumMonthsBetween = (startDate: Date, endDate: Date) => {
  if (startDate > endDate) {
    throw new Error('Start date should be less that the end date');
  }
  const yearsDiff = endDate.getFullYear() - startDate.getFullYear();
  const monthsDiff = endDate.getMonth() - startDate.getMonth();

  return yearsDiff * 12 + monthsDiff;
};

/**
 * gathers all the tax data for each tax rate and name
 * @param invoice An invoice object with invoice lines
 * @returns hash of tax data with tax name and rate
 */
export const getTaxDataByInvoiceLine = (invoice: Invoice) => {
  const taxData: HashOf<{ name: string, rate: number, amount: number }> = (
    invoice.lines.reduce((summary, line) => {
      const taxName = line.taxName ?? invoice.taxName;
      const taxRate = line.taxRate ?? invoice.taxRate;
      const curKey1 = `${taxName}:${taxRate}`;
      if (!taxRate || taxRate === '0') {
        return summary;
      }
      return ({
        ...summary,
        ...({
          [curKey1]: {
            name: taxName,
            rate: formatDecimalToPercentage(taxRate || 0),
            amount: (
              (summary[curKey1]?.amount ?? 0)
              + (
                line.taxAmount
                  ? (roundFloatToCents(floatVal(line.taxAmount || 0)))
                  : (roundFloatToCents(+line.lineTotal) * (floatVal(taxRate) || 0))
              )
            ),
          },
        }),
      });
    }, {} as HashOf<{ name: string, rate: number, amount: number }>));

  const keys = Object.keys(taxData);
  const roundedTaxData = keys.reduce((acc, key) => (
    {
      ...acc,
      [key]: {
        ...taxData[key],
        amount: roundFloatToCents(taxData[key].amount),
      },
    }),
    {} as HashOf<{ name: string, rate: number, amount: number }>);
  return roundedTaxData;
};

/**
 * Creates a where command object for dynamic router queries
 * @param field - The field name to filter on
 * @param value - The value to filter by
 * @param operator - The operator to use (e.g., 'equals', 'like', 'null')
 * @param condition - The condition for multiple where clauses (e.g., 'and', 'or')
 * @param notOperator - Whether to negate the operator
 * @returns IWhereCommand object
 *
 * @example
 * // Simple equality check
 * createWhereCommand('id', '123')
 *
 * @example
 * // OR condition
 * createWhereCommand('status', 'active', undefined, 'or')
 *
 * @example
 * // NOT NULL check
 * createWhereCommand('serviceId', 'null', 'null', undefined, true)
 */
export const createWhereCommand = (
  field: string,
  value: string,
  operator?: TWhereOperator,
  condition?: TWhereCondition,
  notOperator?: boolean,
): IWhereCommand => ({
  field,
  value,
  operator,
  condition,
  notOperator,
});

export const createOrderByCommand = (
  direction: TOrderDirection, field: string,
): IOrderCommand => ({
  direction,
  field,
});

/**
 * Checks if a value is a valid USA or Canadian postal code
 * @param value string to check if valid USA or Canadian postal code
 * @returns true/false
 */
export const isValidPostalCode = (value: string) => {
  const trimmed = value.trim();
  const postalCodeRegex = /^(?:\d{5}(?:-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$/i;
  return postalCodeRegex.test(trimmed);
};
